import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  clearNotifications,
  getStoredNotifications,
  markAllNotificationsRead,
  setNotificationRead,
  subscribeToNotifications,
} from '../../firebase/notificationStore';

function timeAgo(timestamp) {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
];

const EMPTY_MESSAGES = {
  all: 'No notifications yet.',
  unread: "You're all caught up!",
  read: 'No read notifications.',
};

/**
 * NotificationBell
 * ---------------------------------------------------------------------------
 * Header ke topbar wala bell icon + dropdown — push notifications (Firebase,
 * see src/firebase/notificationStore.js) ki list dikhata hai, apna open/close
 * state khud manage karta hai, LiveSupportWidget jaisa hi pattern.
 * All / Unread / Read tabs; notification pe click karne se woh read ho jata
 * hai, aur envelope icon se read/unread toggle hota hai.
 * ---------------------------------------------------------------------------
 */
const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [notifications, setNotifications] = useState(() => getStoredNotifications());
  const wrapperRef = useRef(null);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const counts = {
    all: notifications.length,
    unread: unreadCount,
    read: notifications.length - unreadCount,
  };
  const visibleNotifications = notifications.filter((n) =>
    filter === 'all' ? true : filter === 'unread' ? !n.read : n.read
  );

  useEffect(() => {
    return subscribeToNotifications(setNotifications);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // A click that marks a notification read can remove it from the
      // filtered list (e.g. Unread tab) before this runs — a detached target
      // was inside the dropdown, so don't treat it as an outside click.
      if (!event.target.isConnected) return;
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleToggle = (event) => {
    event.preventDefault();
    setIsOpen((prev) => !prev);
  };

  const handleToggleRead = (event, notification) => {
    event.stopPropagation();
    setNotifications(setNotificationRead(notification.id, !notification.read));
  };

  return (
    <li className="nav-item topbar-icon dropdown hidden-caret" ref={wrapperRef}>
      <Link
        className="nav-link dropdown-toggle"
        to="#"
        role="button"
        title="Notifications"
        aria-expanded={isOpen}
        onClick={handleToggle}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #4a6cf7, #1e3ec0)',
          boxShadow: '0 4px 10px rgba(30, 62, 192, 0.35)',
        }}
      >
        <i className="bx bx-bell" style={{ fontSize: '20px', color: '#fff' }} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              minWidth: '16px',
              height: '16px',
              padding: '0 4px',
              borderRadius: '8px',
              background: '#e74c3c',
              color: '#fff',
              fontSize: '10px',
              fontWeight: 700,
              lineHeight: '16px',
              textAlign: 'center',
              boxShadow: '0 0 0 2px #fff',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Link>
      <div
        className={`dropdown-menu dropdown-support animated fadeIn ${isOpen ? 'show' : ''}`}
        style={{
          // Visibility is driven by inline display, not just the `show` class:
          // public/assets/js/custom.js strips `show` from every .dropdown-menu
          // on any document click, which would hide this on clicks inside it.
          display: isOpen ? 'block' : 'none',
          right: 0,
          left: 'auto',
          width: '370px',
          maxWidth: 'calc(100vw - 32px)',
          padding: 0,
          border: 0,
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 12px 36px rgba(6, 19, 61, 0.2)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px',
            background: 'linear-gradient(135deg, #4a6cf7, #1e3ec0)',
            color: '#fff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h6 style={{ margin: 0, fontWeight: 700, fontSize: '15px', color: '#fff' }}>Notifications</h6>
            {unreadCount > 0 && (
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.22)',
                  fontSize: '11px',
                  fontWeight: 600,
                }}
              >
                {unreadCount} new
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setNotifications(markAllNotificationsRead())}
            disabled={unreadCount === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: 0,
              border: 0,
              background: 'transparent',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 600,
              cursor: unreadCount === 0 ? 'default' : 'pointer',
              opacity: unreadCount === 0 ? 0.5 : 1,
            }}
          >
            <i className="bx bx-check-double" style={{ fontSize: '16px' }} />
            Mark all read
          </button>
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: '6px', padding: '10px 12px', borderBottom: '1px solid #eef0f4' }}>
          {FILTERS.map(({ key, label }) => {
            const active = filter === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '5px 12px',
                  borderRadius: '16px',
                  border: active ? '1px solid #1e3ec0' : '1px solid #e2e6ef',
                  background: active ? '#1e3ec0' : '#fff',
                  color: active ? '#fff' : '#374151',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {label}
                <span
                  style={{
                    minWidth: '18px',
                    padding: '0 5px',
                    borderRadius: '9px',
                    background: active ? 'rgba(255, 255, 255, 0.25)' : '#eef1f8',
                    color: active ? '#fff' : '#4b5563',
                    fontSize: '10px',
                    lineHeight: '16px',
                    textAlign: 'center',
                  }}
                >
                  {counts[key]}
                </span>
              </button>
            );
          })}
        </div>

        {/* List */}
        <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
          {visibleNotifications.length === 0 ? (
            <div style={{ padding: '32px 16px', textAlign: 'center' }}>
              <i className="bx bx-bell-off" style={{ fontSize: '36px', color: '#c3cad9' }} />
              <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#6b7280' }}>
                {EMPTY_MESSAGES[filter]}
              </p>
            </div>
          ) : (
            visibleNotifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => !notification.read && setNotifications(setNotificationRead(notification.id, true))}
                style={{
                  display: 'flex',
                  gap: '12px',
                  padding: '12px 16px',
                  borderBottom: '1px solid #eef0f4',
                  borderLeft: notification.read ? '3px solid transparent' : '3px solid #4a6cf7',
                  background: notification.read ? '#fff' : '#f4f7ff',
                  cursor: notification.read ? 'default' : 'pointer',
                }}
              >
                <div
                  style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: notification.read ? '#eef1f6' : 'linear-gradient(135deg, #4a6cf7, #1e3ec0)',
                  }}
                >
                  <i
                    className="bx bx-bell"
                    style={{ fontSize: '17px', color: notification.read ? '#8a94a6' : '#fff' }}
                  />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: notification.read ? 500 : 700,
                      fontSize: '13px',
                      color: notification.read ? '#4b5563' : '#06133d',
                      wordBreak: 'break-word',
                    }}
                  >
                    {notification.title}
                  </p>
                  {notification.body && (
                    <p
                      style={{
                        margin: '3px 0 0',
                        fontSize: '12px',
                        color: notification.read ? '#6b7280' : '#374151',
                        wordBreak: 'break-word',
                      }}
                    >
                      {notification.body}
                    </p>
                  )}
                  <p
                    title={new Date(notification.receivedAt).toLocaleString()}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', margin: '6px 0 0', fontSize: '11px', color: '#9ca3af' }}
                  >
                    <i className="bx bx-time-five" style={{ fontSize: '13px' }} />
                    {timeAgo(notification.receivedAt)}
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  {!notification.read && (
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4a6cf7' }} />
                  )}
                  <button
                    type="button"
                    title={notification.read ? 'Mark as unread' : 'Mark as read'}
                    onClick={(event) => handleToggleRead(event, notification)}
                    style={{
                      padding: 0,
                      border: 0,
                      background: 'transparent',
                      color: '#8a94a6',
                      cursor: 'pointer',
                      lineHeight: 1,
                    }}
                  >
                    <i
                      className={notification.read ? 'bx bx-envelope' : 'bx bx-envelope-open'}
                      style={{ fontSize: '17px' }}
                    />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 16px', background: '#fafbfd' }}>
            <button
              type="button"
              onClick={() => setNotifications(clearNotifications())}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: 0,
                border: 0,
                background: 'transparent',
                color: '#e74c3c',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <i className="bx bx-trash" style={{ fontSize: '15px' }} />
              Clear all
            </button>
          </div>
        )}
      </div>
    </li>
  );
};

export default NotificationBell;
